import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useRelationship } from 'services/repositories/relationships/relationships';
import { useRelationshipTasks } from 'services/repositories/tasks/tasks';
import { useCurrentUser } from 'services/repositories/user/user';
import { useTaskVariables } from 'services/tasks/tasks';
import { DisplayModes } from 'services/webedi/types';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { useAnalytics } from 'support/helpers/analytics/analytics';
import { getTradePartner } from 'support/helpers/relationships/relationships';
import { TaskDTO } from 'support/types';
import { useConnectionNavigation } from 'pages/Connections/ConnectionDetailsPage/contexts/ConnectionNavigationContext';
import { AlertSection } from 'components/Display/Alert';
import { Alert } from 'components/Display/Alert/Alert';
import { TaskSlideOver as TaskSlideOverBase } from 'components/TaskSlideOver/TaskSlideOver';

import { useTaskSubmission } from './hooks/useTaskSubmission';

export const TaskSlideOver = ({ open }: { open: boolean }) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const { partnerId, relationshipId, taskId } = useParams<{
    partnerId: string;
    relationshipId: string;
    taskId: string;
  }>();
  const { routeTo } = useConnectionNavigation();

  const { data: currentPartner, isLoading: isCurrentPartnerLoading } = useCurrentPartner();
  const { data: currentUser, isLoading: isLoadingCurrentUser } = useCurrentUser();
  const { data: relationship, isLoading: isLoadingRelationship } = useRelationship({
    variables: { relationshipId },
    keepPreviousData: true,
    enabled: Boolean(relationshipId),
  });
  const { data: relationshipTasks, isLoading: isLoadingTasks } = useRelationshipTasks({
    variables: { relationshipId },
    enabled: Boolean(relationshipId),
  });
  const task = relationshipTasks?.data.find((t) => t.id === taskId);

  const isLoading = isCurrentPartnerLoading || isLoadingRelationship || isLoadingTasks || isLoadingCurrentUser;
  const otherPartner = relationship && currentPartner && getTradePartner(relationship, currentPartner.id);
  const mode = getTaskMode(task, currentPartner?.id, currentUser?.isAdmin);
  const variables = useTaskVariables(relationship, task?.responsible_partner?.id);

  const isCompletedByCurrentPartner = isTaskCompletedByPartner(task, currentPartner?.id);

  useEffect(() => {
    if (open && task && !isLoading) {
      track('task:opened', {
        task_id: task.id,
        task_key: task.key,
        task_title: task.title,
        task_state: task.state,
      });
    }
  }, [open, task, isLoading, track]);

  const handleClose = useCallback(
    () =>
      navigate(
        routeTo('relationshipDetails', {
          partnerId: partnerId!,
          relationshipId: relationshipId!,
        }),
      ),
    [navigate, routeTo, partnerId, relationshipId],
  );

  const taskAnalyticsProperties = getTaskAnalyticsProperties(task);

  const handleSuccess = () => {
    track('task:submitted', {
      ...taskAnalyticsProperties,
      relationship_id: relationshipId,
    });
    const isLastPendingTask =
      relationshipTasks?.data.filter((t) => t.state === 'PENDING' || t.state === 'BLOCKED').length === 1 &&
      (task?.state === 'PENDING' || task?.state === 'BLOCKED');
    if (isLastPendingTask) {
      addNotification(
        {
          title: t('connections:relationshipDetails.task.allTasksCompleted.title'),
          subtitle: t('connections:relationshipDetails.task.allTasksCompleted.description'),
        },
        NotificationType.success,
      );
    }
    handleClose();
  };

  const handleError = useCallback(() => {
    track('task:submission_error', {
      ...taskAnalyticsProperties,
      relationship_id: relationshipId,
    });
  }, [track, taskAnalyticsProperties, relationshipId]);

  const handleValidationError = useCallback(
    (errors: Record<string, unknown>, data: Record<string, unknown>) => {
      track('task:validation_error', {
        ...taskAnalyticsProperties,
        errors,
        data,
      });
    },
    [track, taskAnalyticsProperties],
  );

  const { submitTask, isSubmitting } = useTaskSubmission({
    relationshipId: relationshipId!,
    taskId: taskId!,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const topBannerState = getTaskTopBannerState(task);

  return (
    <TaskSlideOverBase
      open={open}
      onClose={handleClose}
      isLoading={isLoading}
      partnerName={otherPartner?.name}
      partnerLogoUrl={otherPartner?.logo_url}
      task={task}
      variables={variables}
      mode={mode}
      isSubmitting={isSubmitting}
      relationshipId={relationship?.id}
      onSubmit={submitTask}
      onValidationError={handleValidationError}
      // t('connections:relationshipDetails.task.inReviewBanner.title')
      // t('connections:relationshipDetails.task.inReviewBanner.description')
      topBanner={topBannerState ? <TaskTopBanner {...topBannerState} /> : undefined}
      extraContent={
        isCompletedByCurrentPartner ? (
          <Alert severity="success">
            <AlertSection
              severity="success"
              title={t('connections:relationshipDetails.task.completedBanner.description')}
            ></AlertSection>
          </Alert>
        ) : undefined
      }
    />
  );
};

const getTaskMode = (
  task: TaskDTO | undefined,
  currentPartnerId: string | undefined,
  isUserAdmin: boolean | undefined,
): DisplayModes => {
  if (!task || !currentPartnerId || (task.responsible_partner?.id !== currentPartnerId && !isUserAdmin)) {
    return DisplayModes.view;
  }

  if (task.state === 'PENDING') {
    return DisplayModes.create;
  }

  if (task.state === 'BLOCKED') {
    return DisplayModes.edit;
  }

  return DisplayModes.view;
};

const TaskTopBanner = ({
  variant,
  rejectionReason,
}: {
  variant: 'blocked' | 'in-review';
  rejectionReason?: string;
}) => {
  const { t } = useTranslation();

  return variant === 'blocked' ? (
    <Alert severity="error" fullWidth withoutIcon title={t('connections:relationshipDetails.task.blockedBanner.title')}>
      <p>{rejectionReason}</p>
    </Alert>
  ) : (
    <Alert
      severity="warning"
      fullWidth
      withoutIcon
      title={t('connections:relationshipDetails.task.inReviewBanner.title')}
    >
      <p>{t('connections:relationshipDetails.task.inReviewBanner.description')}</p>
    </Alert>
  );
};

const getTaskAnalyticsProperties = (task: TaskDTO | undefined) => ({
  task_id: task?.id,
  task_key: task?.key,
  task_title: task?.title,
  task_state: task?.state,
});

const isTaskCompletedByPartner = (task: TaskDTO | undefined, partnerId: string | undefined): boolean =>
  task?.state === 'COMPLETED' && task?.responsible_partner?.id === partnerId;

type TaskTopBannerState = { variant: 'blocked'; rejectionReason: string } | { variant: 'in-review' };

const getTaskTopBannerState = (task: TaskDTO | undefined): TaskTopBannerState | null => {
  if (task?.state === 'BLOCKED' && task.rejection_reason) {
    return { variant: 'blocked', rejectionReason: task.rejection_reason };
  }
  if (task?.state === 'IN_REVIEW') {
    return { variant: 'in-review' };
  }
  return null;
};
